import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";
import { getAuthHeaders, handleApiResponse } from "../utils/apiHelpers";

/** Default BigQuery dataset for pharmacy dashboard search-like calls. */
export const BIGQUERY_SEARCH_LIKE_DATASET_NAME = "pharmacy_pa_requests";

/** Default BigQuery table for pharmacy dashboard search-like calls. */
export const BIGQUERY_SEARCH_LIKE_TABLE_NAME = "pa_request_entries";

export interface BigQuerySearchLikePayload {
  distinct: boolean;
  select_column: string;
  filter_column: string;
  query: string;
  limit: number;
  created_at_cutoff_months: number;
  /** BigQuery dataset; uses {@link BIGQUERY_SEARCH_LIKE_DATASET_NAME} when omitted. */
  dataset_name?: string;
  /** BigQuery table; uses {@link BIGQUERY_SEARCH_LIKE_TABLE_NAME} when omitted. */
  table_name?: string;
}

function normalizeSearchResponse(response: unknown): string[] {
  if (response == null) return [];

  if (Array.isArray(response)) {
    const first = response[0];
    if (first != null && typeof first === "object" && !Array.isArray(first)) {
      return response.map((row) => {
        const obj = row as Record<string, unknown>;
        const val =
          obj.value ??
          obj.label ??
          obj.name ??
          obj.text ??
          Object.values(obj)[0];
        return typeof val === "string" ? val : String(val ?? "");
      });
    }
    return response.map((v) => (typeof v === "string" ? v : String(v)));
  }

  if (typeof response === "object") {
    const obj = response as Record<string, unknown>;

    // Explicit handling: { success, message, rows: [ { drug_name: "..." }, ... ] }
    const rows = obj.rows;
    if (Array.isArray(rows) && rows.length > 0) {
      return rows.map((row) => {
        if (row != null && typeof row === "object" && !Array.isArray(row)) {
          const rowObj = row as Record<string, unknown>;
          const val =
            rowObj.value ??
            rowObj.label ??
            rowObj.drug_name ??
            rowObj.name ??
            rowObj.text ??
            Object.values(rowObj)[0];
          return typeof val === "string" ? val : String(val ?? "");
        }
        return String(row);
      });
    }

    const arr = obj.results ?? obj.data ?? obj.result ?? obj.values;
    if (Array.isArray(arr)) {
      const first = arr[0];
      if (first != null && typeof first === "object" && !Array.isArray(first)) {
        return arr.map((row) => {
          const rowObj = row as Record<string, unknown>;
          const val =
            rowObj.value ??
            rowObj.label ??
            rowObj.name ??
            Object.values(rowObj)[0];
          return typeof val === "string" ? val : String(val ?? "");
        });
      }
      return arr.map((v) => (typeof v === "string" ? v : String(v)));
    }

    for (const key of Object.keys(obj)) {
      const val = obj[key];
      if (Array.isArray(val) && val.length > 0) {
        return normalizeSearchResponse(val);
      }
    }
  }

  return [];
}

export async function bigquerySearchLike(
  payload: BigQuerySearchLikePayload,
  signal?: AbortSignal,
): Promise<string[]> {
  const authHeaders = await getAuthHeaders();
  const headers = new Headers({
    "Content-Type": "application/json",
    ...authHeaders,
  });
  const body = {
    ...payload,
    dataset_name: payload.dataset_name ?? BIGQUERY_SEARCH_LIKE_DATASET_NAME,
    table_name: payload.table_name ?? BIGQUERY_SEARCH_LIKE_TABLE_NAME,
  };
  const bodyString = JSON.stringify(body);

  try {
    const response = await fetch(API_ENDPOINTS.BIGQUERY_SEARCH_LIKE, {
      method: "POST",
      headers,
      body: bodyString,
      signal,
    });
    const data = await handleApiResponse(response);
    logDataToConsole("BigQuery search-like", data);
    return normalizeSearchResponse(data);
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      throw error;
    }
    logError(error as Error, "Error in bigquerySearchLike");
    throw error;
  }
}
